import React, { useState, useEffect } from 'react';
import { styled } from '@uhg-abyss/web/tools/styled';
import { V2TextInput } from '@uhg-abyss/web/ui/TextInput';
import { useFormV2 } from '@uhg-abyss/web/hooks/useForm';
import { IconSymbol } from '@uhg-abyss/web/ui/IconSymbol';
import { useTinRegistration } from '@src/state/tinRegistration';
import { useUser } from '@src/state/user/hooks';

const SectionStack = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
});
const InputWrap = styled('div', { width: '32%' });
const SignaturePreviewBox = styled('div', {
  width: '100%',
  height: '100px',
  borderRadius: '3px',
  marginTop: '8px',
  backgroundColor: '$core.color.neutral.10',
});
const ActionLinksGroup = styled('div', {
  display: 'flex',
  gap: '60px',
  margin: '16px 0 0 30px',
});
const SignatureStatement = styled('p', {
  fontSize: '16px',
  color: '$core.color.neutral.70',
  lineHeight: '1.5',
});
const SignatureActionLink = styled('span', {
  fontSize: '16px',
  fontWeight: 700,
  color: '$info1',
  cursor: 'pointer',
  '&:hover': { color: '$primary1' },
});
const UploadingMessage = styled('div', {
  fontSize: '14px',
  fontWeight: 600,
  color: '$accent1',
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
});
const UploadSuccessMessage = styled('div', {
  fontSize: '14px',
  fontWeight: 600,
  color: '$success1',
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
});
const ErrorMessage = styled('div', {
  fontSize: '14px',
  fontWeight: 600,
  color: '$error1',
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
});

export const TextSignatureTab = ({ activeTab }) => {
  const form = useFormV2();
  const { uploadProof, setDocumentId } = useTinRegistration.actions();
  const { trackingNumber, documentIds } = useTinRegistration.state();
  const registration = form.getValues('searchRegistration');
  const { isCheckoutUser } = useUser.state();
  const textSignature = form.watch('attestation.textSignature');

  const [imgBase64, setImgBase64] = useState('');
  const [uploadLoading, setUploadLoading] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [uploadError, setUploadError] = useState(false);
  const [disableSave, setDisableSave] = useState(false);

  const update = (patch) => {
    if ('imgBase64' in patch) setImgBase64(patch.imgBase64);
    if ('uploadLoading' in patch) setUploadLoading(patch.uploadLoading);
    if ('uploadSuccess' in patch) setUploadSuccess(patch.uploadSuccess);
    if ('uploadError' in patch) setUploadError(patch.uploadError);
    if ('disableSave' in patch) setDisableSave(patch.disableSave);
  };

  const getBinaryFile = (img) => {
    const blobBin = atob(img.split(',')[1]);
    const array = [];
    for (let i = 0; i < blobBin.length; i++) array.push(blobBin.charCodeAt(i));
    return new Blob([new Uint8Array(array)], {
      type: 'image/png',
      encoding: 'utf-8',
    });
  };

  const loadCanvasImage = (text) => {
    const canvasTxt = document
      .getElementById('attest-canvasComponent')
      ?.getContext('2d');
    if (!canvasTxt) return;
    canvasTxt.canvas.width = canvasTxt.measureText(text).width + 30;
    canvasTxt.canvas.height = 100;
    canvasTxt.font = '36px URW Chancery L, cursive';
    canvasTxt.clearRect(0, 0, canvasTxt.canvas.width, canvasTxt.canvas.height);
    if (text && text.length > 0) canvasTxt.fillText(text, 10, 60);
    update({ imgBase64: canvasTxt.canvas.toDataURL('image/png') });
  };

  useEffect(() => {
    loadCanvasImage(` ${textSignature}`);
    if (documentIds?.['attestation.textSignature']) {
      setDocumentId({ id: 'attestation.textSignature', value: undefined });
    }
    update({ uploadSuccess: false, uploadError: false, disableSave: false });
  }, [textSignature]);

  const handleSave = () => {
    update({ uploadSuccess: false, uploadError: false });
    if (
      !textSignature ||
      textSignature.length < 2 ||
      !textSignature.match(/[a-zA-Z]/g)
    ) {
      form.validate('attestation.textSignature');
      return;
    }
    update({ uploadLoading: true });
    const textCanvas = document.getElementById('attest-canvasComponent');
    if (!textCanvas) {
      update({ uploadLoading: false, uploadError: true });
      return;
    }
    const imageBlob = getBinaryFile(textCanvas.toDataURL('image/png'));
    const textFile = new File([imageBlob], `Signature-${trackingNumber}.png`, {
      type: 'image/png',
    });
    uploadProof(
      {
        billingTin: registration?.billingTin,
        requestId: registration?.requestId,
        fileType: 'US',
        trackingNumber,
        file: textFile,
        test: isCheckoutUser,
      },
      (resp) => {
        if (!resp?.doc360Id) {
          update({ uploadLoading: false, uploadError: true });
          return;
        }
        setDocumentId({
          id: 'attestation.textSignature',
          value: resp.doc360Id,
        });
        form.clearErrors('attestation.textSignature');
        update({
          disableSave: true,
          uploadLoading: false,
          uploadSuccess: true,
        });
      },
      () => update({ uploadLoading: false, uploadError: true })
    );
  };

  const handleClear = () => {
    update({
      uploadSuccess: false,
      uploadError: false,
      disableSave: false,
      imgBase64: '',
    });
    form.setValue('attestation.textSignature', '');
    form.clearErrors('attestation.textSignature');
    setDocumentId({ id: 'attestation.textSignature', value: undefined });
  };

  return (
    <SectionStack>
      <InputWrap>
        <V2TextInput
          id="attestation-full-name-signature"
          model="attestation.textSignature"
          label="Full name"
          type="text"
          maxLength={60}
          formatter={(value) =>
            value.replace(/[-!@#$%^&*)({}<>,.[\]+_`~=\\|';":/?0-9]/g, '')
          }
          validators={{
            required: activeTab === 0 ? 'Full name is required.' : '',
            validate: (value) =>
              activeTab === 0 &&
              (!value?.match(/[a-zA-Z]/g) || value.length < 2)
                ? 'Please enter a valid full name.'
                : true,
          }}
        />
      </InputWrap>
      <div>
        <label style={{ fontSize: '16px', fontWeight: 500 }}>
          Digital signature preview
        </label>
        <SignaturePreviewBox>
          <canvas id="attest-canvasComponent" style={{ display: 'none' }} />
          <img
            id="attest-imageComponent"
            style={{ maxWidth: 'none' }}
            src={imgBase64}
            alt=""
          />
        </SignaturePreviewBox>
      </div>
      {!!textSignature && !documentIds?.['attestation.textSignature'] && (
        <ErrorMessage>
          <IconSymbol
            icon="error"
            variant="outlined"
            color="$error1"
            size="20px"
          />
          <span>Please save the signature before submitting.</span>
        </ErrorMessage>
      )}
      {uploadLoading && (
        <UploadingMessage>Saving signature, please wait...</UploadingMessage>
      )}
      {uploadSuccess && (
        <UploadSuccessMessage>
          <IconSymbol
            icon="check_circle"
            variant="outlined"
            color="$success1"
            size="20px"
          />
          <span>Signature has been saved successfully.</span>
        </UploadSuccessMessage>
      )}
      {uploadError && (
        <ErrorMessage>
          <IconSymbol
            icon="error"
            variant="outlined"
            color="$error1"
            size="20px"
          />
          <span>Failed to save signature, please try again.</span>
        </ErrorMessage>
      )}
      <SignatureStatement>
        I agree that the signature will be the electronic representation of my
        signature for all purposes when I use them on documents, including
        legally binding contracts - just the same as a pen-and-paper signature
        or initial.
      </SignatureStatement>
      <ActionLinksGroup>
        <SignatureActionLink
          onClick={handleSave}
          style={{
            opacity: disableSave ? 0.4 : 1,
            pointerEvents: disableSave ? 'none' : 'auto',
          }}
        >
          Save signature
        </SignatureActionLink>
        <SignatureActionLink onClick={handleClear}>
          Clear signature
        </SignatureActionLink>
      </ActionLinksGroup>
    </SectionStack>
  );
};
